/**
 * Expenses Form Section
 * Manages recurring expenses
 */

import * as React from "react";
import { DomainExpenseType } from "@porchpass/api-client";
import {
  Add01Icon,
  Button,
  Delete03Icon,
  ExpenseFields,
  FormTable,
  FormTableAddButton,
  FormTableBody,
  MoneySecurityIcon,
  useContainerRadiusSync,
  useFormTableAddButtonState,
} from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type { Expense } from "../types/applicationTypes";
import {
  validateDependentsAges,
  validateExpenseField,
} from "../utils/validationSchemas";

interface ExpensesFormProps {
  isCoBorrower?: boolean;
}

const createEmptyExpense = (): Expense => ({
  id: crypto.randomUUID(),
  type: "" as DomainExpenseType,
  monthlyAmount: undefined,
});

function isOngoingExpenseType(type: string): boolean {
  return (
    type === DomainExpenseType.ExpenseChildSupport ||
    type === DomainExpenseType.ExpenseAlimony ||
    type === DomainExpenseType.ExpenseGarnishment ||
    type === DomainExpenseType.ExpenseOtherSupport
  );
}

export const ExpensesForm: React.FC<ExpensesFormProps> = ({
  isCoBorrower = false,
}) => {
  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const borrowerType = isCoBorrower ? "co" : "primary";
  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const [expenseForms, setExpenseForms] = React.useState<Expense[]>([]);

  const expenses = borrower?.expenses || [];

  React.useEffect(() => {
    if (expenses.length > 0 && expenseForms.length === 0) {
      setExpenseForms([...expenses]);
    }
  }, [expenses.length]);

  if (!borrower) return null;

  const getErrors = (index: number): Record<string, string> => {
    const prefix = `${borrowerPrefix}.expenses.${index}.`;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(prefix)) {
        result[k.slice(prefix.length)] = v;
      }
    }
    return result;
  };

  const applyExpenseUpdate = (index: number, updates: Partial<Expense>) => {
    const current = expenseForms[index];
    const updated = { ...current, ...updates };
    const newForms = [...expenseForms];
    newForms[index] = updated;
    setExpenseForms(newForms);
    store.updateExpense(borrowerType, index, updated);
  };

  const handleAddExpense = () => {
    const newExpense = createEmptyExpense();
    store.addExpense(borrowerType, newExpense);
    setExpenseForms([...expenseForms, newExpense]);
  };

  const updateExpenseField = <K extends keyof Expense>(
    index: number,
    field: K,
    value: Expense[K],
  ) => {
    if (typeof value === "string" || typeof value === "number") {
      const errorKey = `${borrowerPrefix}.expenses.${index}.${String(field)}`;
      if (
        store.fieldErrors[errorKey] &&
        !validateExpenseField(String(field), String(value))
      ) {
        store.clearFieldError(errorKey);
      }
    }
    if (field === "childrenAges" && Array.isArray(value)) {
      const errorKey = `${borrowerPrefix}.expenses.${index}.childrenAges`;
      if (store.fieldErrors[errorKey] && !validateDependentsAges(value)) {
        store.clearFieldError(errorKey);
      }
    }
    applyExpenseUpdate(index, { [field]: value });
  };

  const handleBulkChange = (index: number, updates: Partial<Expense>) => {
    const errPrefix = `${borrowerPrefix}.expenses.${index}`;
    const current = expenseForms[index];
    const newType = updates.type ?? current.type;
    const wasChildSupport =
      current.type === DomainExpenseType.ExpenseChildSupport;
    const nowChildSupport = newType === DomainExpenseType.ExpenseChildSupport;
    const wasOngoingType = isOngoingExpenseType(current.type);
    const nowOngoingType = isOngoingExpenseType(newType);

    if (updates.type && store.fieldErrors[`${errPrefix}.type`]) {
      store.clearFieldError(`${errPrefix}.type`);
    }

    const mergedUpdates: Partial<Expense> = {
      ...updates,
      ...(!nowOngoingType && wasOngoingType ? { isOngoing: undefined } : {}),
    };

    if (!nowChildSupport && wasChildSupport) {
      ["startMonth", "startYear", "childrenAges"].forEach((f) =>
        store.clearFieldError(`${errPrefix}.${f}`),
      );
    }

    applyExpenseUpdate(index, mergedUpdates);
  };

  const handleDeleteExpense = (index: number) => {
    store.removeExpense(borrowerType, index);
    setExpenseForms(expenseForms.filter((_, i) => i !== index));
  };

  const renderExpenseForm = (expense: Expense, index: number) => {
    const isChildSupport =
      expense.type === DomainExpenseType.ExpenseChildSupport;
    const hasType = !!expense.type;

    return (
      <FormTableBody key={expense.id ?? index}>
        <div className="flex justify-between items-center mb-4">
          <p className="text-base font-medium text-[#111827]">
            Expense Details
          </p>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => handleDeleteExpense(index)}
          >
            <Delete03Icon size={16} className="text-red-500" />
          </Button>
        </div>

        <ExpenseFields
          expense={expense}
          showTypeSelect
          showTypeOther={expense.type === DomainExpenseType.ExpenseOtherSupport}
          showStartDate={isChildSupport}
          showEndDate={hasType}
          showOngoing={hasType}
          showChildrenAges={isChildSupport}
          errors={getErrors(index)}
          onFieldChange={(field, value) =>
            updateExpenseField(index, field, value)
          }
          onBulkChange={(updates) => handleBulkChange(index, updates)}
        />
      </FormTableBody>
    );
  };

  return (
    <FormTable
      title="Expenses"
      headerIcon={
        <MoneySecurityIcon
          variant="bulk-rounded"
          size={16}
          className="text-[#017C3F]"
        />
      }
      style={containerStyles.style}
    >
      {expenseForms.map((expense, index) => renderExpenseForm(expense, index))}

      <FormTableAddButton
        onClick={handleAddExpense}
        {...addButtonState.interactionProps}
      >
        <Add01Icon variant="stroke-rounded" size={12} />
        {expenseForms.length === 0 ? "Add expense" : "Additional expense"}
      </FormTableAddButton>
    </FormTable>
  );
};

export default ExpensesForm;
